import React from "react";

import {
    fetchRecommendations,
    fetchListenTabData,
    Recommendation,
    ListenTabData,
} from "../api/albums";
import { fetchClerkUsersByExternalIds, ClerkUser } from "../api/clerk";

function groupByAlbum(recommendations: Recommendation[]) {
    const groups = new Map<
        number,
        { album_id: number; recommendations: Recommendation[] }
    >();

    for (const rec of recommendations) {
        const existing = groups.get(rec.album_id);

        if (existing) {
            existing.recommendations.push(rec);
            continue;
        }

        groups.set(rec.album_id, { album_id: rec.album_id, recommendations: [rec] });
    }

    return Array.from(groups.values());
}

function formatSentToFriendsCount(recommendations: Recommendation[]) {
    const friendCount = new Set(recommendations.map((rec) => rec.to_user_id)).size;

    if (friendCount === 1) {
        return "Sent to 1 friend";
    }

    return `Sent to ${friendCount} friends`;
}

function buildUsersByExternalId(recommenderIds: string[]) {
    return fetchClerkUsersByExternalIds(recommenderIds).then((recommenders) =>
        Object.fromEntries(
            recommenders
                .filter((user) => user.externalId)
                .map((user) => [user.externalId!, user])
        )
    );
}

const emptyListenTabData: ListenTabData = {
    queue_next: null,
    listened_today: null,
    history: [],
};

export function useProfileTabData(
    externalId: string | null | undefined,
    isCurrentUser: boolean
) {
    const [sent, setSent] = React.useState<Recommendation[]>([]);
    const [sentSubtitleByAlbumId, setSentSubtitleByAlbumId] = React.useState<
        Record<number, string>
    >({});
    const [isLoadingSharedTab, setIsLoadingSharedTab] = React.useState(false);
    const hasLoadedSharedTabRef = React.useRef(false);

    const [listenTabData, setListenTabData] =
        React.useState<ListenTabData>(emptyListenTabData);
    const [queueRecommendations, setQueueRecommendations] = React.useState<
        Recommendation[]
    >([]);
    const [doneRecommendations, setDoneRecommendations] = React.useState<
        Recommendation[]
    >([]);
    const [usersByExternalId, setUsersByExternalId] = React.useState<
        Record<string, ClerkUser>
    >({});
    const [isLoadingListenTab, setIsLoadingListenTab] = React.useState(false);
    const hasLoadedListenTabRef = React.useRef(false);

    const refreshSharedTab = React.useCallback(
        async (options?: { showLoading?: boolean }) => {
            if (!externalId) {
                setSent([]);
                setSentSubtitleByAlbumId({});
                hasLoadedSharedTabRef.current = false;
                setIsLoadingSharedTab(false);
                return;
            }

            const showLoading = options?.showLoading ?? !hasLoadedSharedTabRef.current;
            if (showLoading) {
                setIsLoadingSharedTab(true);
            }

            try {
                const sentData = await fetchRecommendations(externalId, {
                    direction: "sent",
                });
                setSent(sentData);

                const sentSubtitles: Record<number, string> = {};
                for (const group of groupByAlbum(sentData)) {
                    sentSubtitles[group.album_id] = formatSentToFriendsCount(
                        group.recommendations
                    );
                }
                setSentSubtitleByAlbumId(sentSubtitles);
                hasLoadedSharedTabRef.current = true;
            } catch (error) {
                console.error("Error loading recommendations:", error);
                setSent([]);
                setSentSubtitleByAlbumId({});
            } finally {
                setIsLoadingSharedTab(false);
            }
        },
        [externalId]
    );

    const refreshListenTab = React.useCallback(
        async (options?: { showLoading?: boolean }) => {
            if (!externalId) {
                setListenTabData(emptyListenTabData);
                setQueueRecommendations([]);
                setDoneRecommendations([]);
                setUsersByExternalId({});
                hasLoadedListenTabRef.current = false;
                setIsLoadingListenTab(false);
                return;
            }

            const showLoading = options?.showLoading ?? !hasLoadedListenTabRef.current;
            if (showLoading) {
                setIsLoadingListenTab(true);
            }

            try {
                if (isCurrentUser) {
                    const [listenData, queueData, doneData] = await Promise.all([
                        fetchListenTabData(externalId),
                        fetchRecommendations(externalId, {
                            direction: "received",
                            status: "queue",
                        }),
                        fetchRecommendations(externalId, {
                            direction: "received",
                            status: "done",
                        }),
                    ]);

                    setListenTabData(listenData);
                    setQueueRecommendations(queueData);
                    setDoneRecommendations(doneData);

                    const recommenderIds = [
                        ...new Set(
                            [
                                listenData.queue_next,
                                listenData.listened_today,
                                ...listenData.history,
                                ...queueData,
                                ...doneData,
                            ]
                                .filter(Boolean)
                                .map((rec) => String(rec!.from_user_id))
                        ),
                    ];
                    setUsersByExternalId(await buildUsersByExternalId(recommenderIds));
                    hasLoadedListenTabRef.current = true;
                    return;
                }

                const doneData = await fetchRecommendations(externalId, {
                    direction: "received",
                    status: "done",
                });

                const listenData = await fetchListenTabData(externalId);

                setListenTabData({
                    queue_next: null,
                    listened_today: listenData.listened_today,
                    history: doneData,
                });
                setQueueRecommendations([]);
                setDoneRecommendations(doneData);

                const recommenderIds = [
                    ...new Set(
                        [
                            listenData.listened_today,
                            ...doneData,
                        ]
                            .filter(Boolean)
                            .map((rec) => String(rec!.from_user_id))
                    ),
                ];
                setUsersByExternalId(await buildUsersByExternalId(recommenderIds));
                hasLoadedListenTabRef.current = true;
            } catch (error) {
                console.error("Error loading listen tab:", error);
                setListenTabData(emptyListenTabData);
                setQueueRecommendations([]);
                setDoneRecommendations([]);
                setUsersByExternalId({});
            } finally {
                setIsLoadingListenTab(false);
            }
        },
        [externalId, isCurrentUser]
    );

    React.useEffect(() => {
        setSent([]);
        setSentSubtitleByAlbumId({});
        hasLoadedSharedTabRef.current = false;
        setListenTabData(emptyListenTabData);
        setQueueRecommendations([]);
        setDoneRecommendations([]);
        setUsersByExternalId({});
        hasLoadedListenTabRef.current = false;
    }, [externalId]);

    React.useEffect(() => {
        refreshSharedTab();
    }, [refreshSharedTab]);

    React.useEffect(() => {
        refreshListenTab();
    }, [refreshListenTab]);

    return {
        sent,
        sentSubtitleByAlbumId,
        isLoadingSharedTab,
        listenTabData,
        queueRecommendations,
        doneRecommendations,
        usersByExternalId,
        isLoadingListenTab,
        refreshSharedTab,
        refreshListenTab,
    };
}
